import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { FormattedMessage as T, useIntl } from "react-intl";

type EnrollmentDetailsFormProps = {
  disabled: boolean;
  endDate: Date | null;
  onEndDateChange: (value: Date | null) => void;
  crmOrderName: string;
  crmOrderNameError?: string;
  onCrmOrderNameChange: (value: string) => void;
  isTest: boolean;
  onIsTestChange: (value: boolean) => void;
  bypassSkuExpiration: boolean;
  onBypassSkuExpirationChange: (value: boolean) => void;
};

const EnrollmentDetailsForm = ({
  disabled,
  endDate,
  onEndDateChange,
  crmOrderName,
  crmOrderNameError,
  onCrmOrderNameChange,
  isTest,
  onIsTestChange,
  bypassSkuExpiration,
  onBypassSkuExpirationChange,
}: EnrollmentDetailsFormProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
    >
      <Typography variant="h6" gutterBottom>
        <T id="enrollmentDetails" />
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <DatePicker
            label={t({ id: "endDateLabel" })}
            format="MM/dd/yyyy"
            value={endDate}
            onChange={onEndDateChange}
            slotProps={{ textField: { variant: "standard", fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="standard"
            label={t({ id: "crmOrderName" })}
            value={crmOrderName}
            onChange={(e) => onCrmOrderNameChange(e.target.value)}
            error={!!crmOrderNameError}
            helperText={crmOrderNameError}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={isTest} onChange={(e) => onIsTestChange(e.target.checked)} />
            }
            label={
              <Box display="flex" alignItems="center">
                {t({ id: "isTestLabel" })}
                <Tooltip title={t({ id: "isTestHelperText" })}>
                  <InfoIcon fontSize="small" sx={{ ml: 0.5 }} />
                </Tooltip>
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bypassSkuExpiration}
                onChange={(e) => onBypassSkuExpirationChange(e.target.checked)}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                {t({
                  id: "bulkRegistrationBypassExpiration",
                  defaultMessage: "Bypass SKU Expiration Date",
                })}
                <Tooltip
                  title={t({
                    id: "bulkRegistrationBypassExpirationTooltip",
                    defaultMessage:
                      "By toggling this feature on you'll be able to register users to skus that are already expired. Normally this should not happen.",
                  })}
                >
                  <InfoIcon fontSize="small" sx={{ ml: 0.5 }} />
                </Tooltip>
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EnrollmentDetailsForm;
